/**
 * 邀请码纯函数层 —— Proxy（服务端）与客户端共用，禁止引入浏览器 API。
 *
 * **命名约定**：代码标识符一律用 `invite`（对齐后端 `OS-Invite-Code`、线上路由 `/task/invite/:code`、
 * query `?inviteCode=` 这三个既成契约），**用户可见文案**才叫 "Referral code"（走 i18n，与标识符解耦）。
 * 不要在代码里混用 `referral` —— 两端命名必须一致，否则同一条链接会解出不同的码。
 *
 * 后端契约（`opensocial`）：
 * - 绑定走请求头 `OS-Invite-Code`（`OspConstants.HEADER_OS_INVITE_CODE`），
 *   由 `OspAbstractFilter` 写入 `RequestHeaderContext`，注册时 `AuthenticationApiDelegateImpl.getInviteCode()` 消费。
 * - **只在首次注册生效**：已注册用户带上此头不会重新绑定关系。
 * - 后端按「用户邀请码 → 推广码」顺序级联解析同一个值，客户端**不需要**区分类型。
 * - **大小写敏感**：后端 `getByUniqueCode` 直接精确匹配，故本层只 `trim`，
 *   **不得** `toUpperCase()`（与 `adSource` 的处理方式不同，勿照抄）。
 */

/** Cookie key：邀请码（跨会话存活，因为「点链接」与「完成注册」常常不在同一次会话）。 */
export const INVITE_CODE_COOKIE_KEY = "edgen_invite_code";

/** 邀请码在 URL query 上的参数名（`?inviteCode=XXXX`，与既有 `BasicAuth` 透传的 key 一致）。 */
export const INVITE_CODE_QUERY_KEY = "inviteCode";

/**
 * 前端只做**格式**兜底校验，有效性以后端为准（无独立校验端点时无效码会被静默忽略）。
 * 放宽到字母数字 + `-` `_`，长度 1–32：邀请码由 kmember 生成，前端不硬编码其具体规则。
 */
const INVITE_CODE_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

/** 归一化：仅去首尾空白（**不改大小写**，后端精确匹配）。空值返回 null。 */
export function normalizeInviteCode(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** 格式是否合法（仅本地兜底，真实有效性由后端裁定）。 */
export function isValidInviteCodeFormat(
  value: string | null | undefined,
): boolean {
  const normalized = normalizeInviteCode(value);
  return normalized !== null && INVITE_CODE_PATTERN.test(normalized);
}

/** 从原生深链 payload 提取邀请码（`businessQuery.inviteCode`）。 */
export function extractInviteCodeFromDeepLink(
  data: { businessQuery?: Record<string, string> } | null | undefined,
): string | null {
  return normalizeInviteCode(data?.businessQuery?.[INVITE_CODE_QUERY_KEY]);
}

/**
 * 从邀请落地页路径提取邀请码：`/task/invite/<code>`（可带 locale / device 前缀）。
 * 只认这一种 path 形态 —— 深链兜底路由丢 query，故邀请码以 path 段承载。
 */
export function extractInviteCodeFromPathname(
  pathname: string | null | undefined,
): string | null {
  if (!pathname) {
    return null;
  }
  const match = /\/task\/invite\/([^/?#]+)/.exec(pathname);
  if (!match) {
    return null;
  }
  return normalizeInviteCode(decodeURIComponent(match[1]));
}

/**
 * 邀请落地页路径（入参须为 `normalizeProductPathname` 之后的路径，无 locale / device 前缀）：
 * - `/task/invite/<code>` —— 邀请码落地页
 * - `/aura/<handle>` —— 邀请人主页落地页（唯一的 `/aura/*` 路由，同样渲染 AuraInviteLandingPage）
 *
 * **这两条必须免登录可达**：邀请码由 path 段承载，而 Proxy 的 307 目标是固定的 `/login`
 * （`ResponseBuilder` 的 `preserveSearch` 只保 query，保不住 path）。一旦被门禁拦下，
 * 码会连同路径一起消失 —— 而没有 `edgen_onboarding_step` cookie 的人正是「未登录的被邀请人」，
 * 也就是邀请链接唯一的目标人群，等于对他们 100% 失效。
 *
 * 客户端门禁（`useRouteProtection`）早已豁免这两条，此处补齐 Proxy 侧口径。
 */
const INVITE_LANDING_PATTERNS = [
  /^\/task\/invite\/[^/]+\/?$/,
  /^\/aura\/[^/]+\/?$/,
] as const;

/** 是否为邀请落地页（免登录可达）。空 code / 空 handle 不算，仍走登录门禁。 */
export function isInviteLandingPath(
  normalizedPath: string | null | undefined,
): boolean {
  if (!normalizedPath) {
    return false;
  }
  return INVITE_LANDING_PATTERNS.some((pattern) =>
    pattern.test(normalizedPath),
  );
}
